// src/pages/Portfolio.jsx - NEVXOAULIO VENTURES PRODUCT ROADMAP (Mobile-First Optimized)
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Filter,
  ExternalLink,
  Calendar,
  Rocket,
  Lock,
  Clock,
  Zap,
  Brain,
  Database,
  Shield,
  AlertTriangle,
  Mail,
  X,
  ChevronRight,
  TrendingUp
} from 'lucide-react';
import { PRODUCTS } from '../utils/constants';

export default function Portfolio() {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = useMemo(() => ['all', 'Foundation', 'Expansion', 'Advanced', 'Moonshot'], []);

  const filteredProducts = useMemo(() => {
    return selectedCategory === 'all'
      ? PRODUCTS
      : PRODUCTS.filter(p => p.category === selectedCategory);
  }, [selectedCategory]);

  const getCategoryColor = (category) => {
    const colors = {
      Foundation: 'green',
      Expansion: 'blue',
      Advanced: 'purple',
      Moonshot: 'orange'
    };
    return colors[category] || 'gray';
  };

  const getCategoryBgColor = (category) => {
    const colors = {
      Foundation: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      Expansion: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      Advanced: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      Moonshot: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const stats = [
    { value: PRODUCTS.length, label: 'Products Planned', icon: '🚀', color: 'blue' },
    { value: '10 Years', label: 'Development Timeline', icon: '⏱️', color: 'purple' },
    { value: '4 Phases', label: 'Strategic Roadmap', icon: '📊', color: 'indigo' },
    { value: '$10-$100M+', label: 'Investment Required', icon: '💰', color: 'green' }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 z-0 dark:hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl opacity-70 animate-pulse" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(to right, rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>
      <div className="fixed inset-0 z-0 hidden dark:block bg-gray-900" />

      <div className="relative z-10">
        {/* ===== HERO SECTION - Enhanced for Mobile ===== */}
        <section className="relative py-12 md:py-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div className="flex items-center space-x-3 mb-4 md:mb-6">
                  <Rocket className="w-10 h-10 md:w-12 md:h-12" />
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold">Product Roadmap</h1>
                </div>
                <p className="text-xl md:text-2xl text-blue-100 mb-4 md:mb-6 font-semibold">
                  NEVXOAULIO VENTURES: Making India Self-Reliant in Technology
                </p>
                <p className="text-base md:text-lg text-blue-100 mb-6 md:mb-8 leading-relaxed max-w-3xl">
                  22 innovative products across AI, Cybersecurity, Blockchain, Quantum Computing, Robotics, and Semiconductor Manufacturing - transforming India into a global tech powerhouse.
                </p>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 min-h-[44px]">
                    <Clock className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                    <span className="font-bold text-sm md:text-base">Timeline: 2030-2040</span>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-white/20 backdrop-blur-sm rounded-full border border-white/30 min-h-[44px]">
                    <Zap className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                    <span className="font-bold text-sm md:text-base">{PRODUCTS.length} Products</span>
                  </div>
                </div>
              </div>
              <div className="hidden lg:flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-2xl opacity-30"></div>
                  <div className="relative bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl p-8 text-center">
                    <TrendingUp className="w-24 h-24 mx-auto mb-4 text-blue-200" />
                    <p className="text-xl font-bold">Vision 2040</p>
                    <p className="text-sm text-blue-100 mt-2">India as Global Tech Leader</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SECURITY ALERT BANNER - Mobile Optimized ===== */}
        <section className="py-4 md:py-6 bg-gradient-to-r from-red-500 via-orange-500 to-red-600 border-y-4 border-red-700 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4 text-white">
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/40 animate-pulse">
                  <AlertTriangle className="w-5 h-5 md:w-8 md:h-8" />
                </div>
                <Shield className="w-6 h-6 md:w-8 md:h-8 hidden sm:block" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold mb-1 md:mb-2 flex items-center justify-center md:justify-start gap-2">
                  <Lock className="w-4 h-4 md:w-6 md:h-6 flex-shrink-0" />
                  <span>Security Notice: Confidential Projects Protected</span>
                </h3>
                <p className="text-sm md:text-base lg:text-lg font-semibold leading-relaxed">
                  Our completed client projects and live deployments are kept confidential. We protect client privacy and data security. Contact us for case studies and project details after verification.
                </p>
              </div>
              <a
                href="/contact"
                className="px-4 py-2 md:px-6 md:py-3 bg-white text-red-600 rounded-lg font-bold hover:bg-gray-100 transition-all shadow-lg flex items-center justify-center gap-2 whitespace-nowrap flex-shrink-0 text-sm md:text-base min-h-[44px] w-full md:w-auto"
              >
                <Mail className="w-4 h-4 md:w-5 md:h-5" />
                Request Portfolio
              </a>
            </div>
          </div>
        </section>

        {/* ===== STATS BANNER - Responsive Grid ===== */}
        <section className="py-6 md:py-8 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center p-3 md:p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 rounded-lg">
                  <div className="text-2xl md:text-3xl mb-1 md:mb-2">{stat.icon}</div>
                  <div className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                  <div className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== TIMELINE SECTION - Mobile Stack ===== */}
        <section className="py-12 md:py-16 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 dark:text-white mb-6 md:mb-8">
              Our 10-Year Development Timeline
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { years: "2030-2031", phase: "Foundation Phase", desc: "7 Products - Core AI & Blockchain", color: "green", icon: "🚀" },
                { years: "2032-2033", phase: "Expansion Phase", desc: "5 Products - Advanced Solutions", color: "blue", icon: "📈" },
                { years: "2034-2036", phase: "Advanced Phase", desc: "5 Products - Quantum & Robotics", color: "purple", icon: "🧬" },
                { years: "2037-2040", phase: "Moonshot Phase", desc: "5 Products - Breakthrough Tech", color: "orange", icon: "⭐" }
              ].map((timeline, idx) => (
                <div
                  key={idx}
                  className={`bg-white dark:bg-gray-800 rounded-2xl p-4 md:p-6 shadow-lg border-4 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 md:hover:-translate-y-2 ${timeline.color === 'green' ? 'border-green-500' : timeline.color === 'blue' ? 'border-blue-500' : timeline.color === 'purple' ? 'border-purple-500' : 'border-orange-500'}`}
                >
                  <div className="text-3xl md:text-4xl mb-2 md:mb-3">{timeline.icon}</div>
                  <div className={`text-2xl md:text-3xl font-bold mb-1 md:mb-2 ${timeline.color === 'green' ? 'text-green-600' : timeline.color === 'blue' ? 'text-blue-600' : timeline.color === 'purple' ? 'text-purple-600' : 'text-orange-600'}`}>
                    {timeline.years}
                  </div>
                  <div className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-1 md:mb-2">
                    {timeline.phase}
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                    {timeline.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== KEY TECHNOLOGIES BANNER - Mobile Wrap ===== */}
        <section className="py-6 md:py-8 bg-white dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-center text-base md:text-lg font-bold text-gray-900 dark:text-white mb-4 md:mb-6">
              Core Technologies Across All Products
            </h3>
            <div className="flex flex-wrap justify-center gap-2 md:gap-4">
              {[
                { icon: '🤖', text: 'AI & ML' },
                { icon: '⛓️', text: 'Blockchain' },
                { icon: '🔐', text: 'Cybersecurity' },
                { icon: '☁️', text: 'Cloud' },
                { icon: '📊', text: 'Data Science' },
                { icon: '🧬', text: 'Quantum' },
                { icon: '🤖', text: 'Robotics' },
                { icon: '🔌', text: 'VLSI' }
              ].map((tech, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1 md:gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full shadow-md font-semibold text-xs md:text-sm"
                >
                  <span>{tech.icon}</span>
                  <span>{tech.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== MOBILE-OPTIMIZED FILTER SECTION ===== */}
        <section className="sticky top-0 z-40 py-4 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-md">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2 text-gray-700 dark:text-gray-300">
                <Filter className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                <span className="font-bold text-sm md:text-base">Filter by Phase:</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 md:hidden">Swipe →</span>
            </div>
            
            <div className="flex overflow-x-auto pb-2 md:overflow-visible md:flex-wrap gap-2 scrollbar-hide">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`flex-shrink-0 px-4 py-2.5 md:px-6 md:py-3 rounded-lg font-bold transition-all shadow-md text-sm md:text-base min-h-[44px] flex items-center ${selectedCategory === cat
                      ? 'bg-blue-600 text-white shadow-lg scale-105'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                >
                  {cat === 'all' ? 'All Phases' : cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* ===== RESPONSIVE PRODUCTS GRID ===== */}
        <section className="py-12 md:py-16 bg-gradient-to-br from-white to-blue-50 dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3">
                {selectedCategory === 'all'
                  ? `All ${PRODUCTS.length} Products - NEVXOAULIO VENTURES`
                  : `${selectedCategory} Phase Products`}
              </h2>
              <p className="text-base md:text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
                {filteredProducts.length} innovative products transforming India's tech landscape
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {filteredProducts.map((product, idx) => (
                <div
                  key={idx}
                  className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 md:hover:-translate-y-2 border-2 border-dashed border-blue-300 dark:border-blue-600 relative group"
                >
                  <div className={`absolute top-3 right-3 md:top-4 md:right-4 px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs font-bold flex items-center space-x-1 shadow-lg z-10 ${getCategoryBgColor(product.category)}`}>
                    <Calendar className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                    <span>{product.year}</span>
                  </div>

                  <div className="p-4 md:p-6">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3 md:mb-4 line-clamp-2">
                      {product.name}
                    </h3>

                    <div className="flex flex-wrap gap-2 mb-3 md:mb-4">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${getCategoryBgColor(product.category)}`}>
                        Phase: {product.category}
                      </span>
                    </div>

                    <p className="text-gray-600 dark:text-gray-300 mb-3 md:mb-4 line-clamp-3 leading-relaxed font-medium text-sm md:text-base">
                      {product.description}
                    </p>

                    {product.features && product.features.length > 0 && (
                      <div className="mb-3 md:mb-4">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-1 md:mb-2 text-xs md:text-sm flex items-center gap-1 md:gap-2">
                          <Zap className="w-3 h-3 md:w-4 md:h-4 text-blue-600 flex-shrink-0" />
                          Key Features:
                        </h4>
                        <ul className="space-y-1">
                          {product.features.slice(0, 3).map((feature, featureIdx) => (
                            <li
                              key={featureIdx}
                              className="text-xs md:text-sm text-gray-600 dark:text-gray-400 flex items-start"
                            >
                              <span className="text-blue-500 mr-1 md:mr-2 font-bold flex-shrink-0 mt-0.5">✓</span>
                              <span className="line-clamp-2">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {product.techStack && product.techStack.length > 0 && (
                      <div className="mb-3 md:mb-4">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-1 md:mb-2 text-xs md:text-sm">
                          Technology:
                        </h4>
                        <div className="flex flex-wrap gap-1 md:gap-2">
                          {product.techStack.slice(0, 3).map((tech, techIdx) => (
                            <span
                              key={techIdx}
                              className="px-2 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 text-blue-700 dark:text-blue-300 text-xs rounded-full font-semibold border border-blue-200 dark:border-blue-800"
                            >
                              {tech}
                            </span>
                          ))}
                          {product.techStack.length > 3 && (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full font-semibold">
                              +{product.techStack.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {product.pricing && (
                      <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Expected Pricing:
                        </div>
                        <div className="text-base md:text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                          {product.pricing}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => setSelectedProject(product)}
                      className="mt-4 md:mt-6 w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 text-sm md:text-base min-h-[44px]"
                    >
                      <span>View Full Details</span>
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-12 md:py-20">
                <Rocket className="w-16 h-16 md:w-20 md:h-20 text-gray-400 mx-auto mb-3 md:mb-4" />
                <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400">
                  No products planned in this phase yet.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* ===== FULL-SCREEN MOBILE MODAL ===== */}
        {selectedProject && (
          <div
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-start md:items-center justify-center p-0 md:p-4 overflow-y-auto"
            onClick={() => setSelectedProject(null)}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl w-full md:max-w-4xl md:w-full h-screen md:h-auto md:max-h-[90vh] md:my-8 shadow-2xl overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white dark:bg-gray-800 px-4 md:px-8 py-4 md:py-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between z-20 md:rounded-t-2xl">
                <div className="flex flex-wrap items-center gap-1 md:gap-2 flex-1">
                  <span className={`px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs font-bold ${getCategoryBgColor(selectedProject.category)}`}>
                    {selectedProject.category}
                  </span>
                  <span className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-2 md:px-3 py-1 md:py-1.5 rounded-full text-xs font-bold flex items-center space-x-1">
                    <Calendar className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                    <span>Year {selectedProject.year}</span>
                  </span>
                </div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="w-10 h-10 md:w-10 md:h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0 ml-1 md:ml-2 min-w-[40px] min-h-[40px]"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 md:w-6 md:h-6" />
                </button>
              </div>

              <div className="px-4 md:px-8 py-4 md:py-6 space-y-4 md:space-y-6">
                <div>
                  <h2 className="text-xl md:text-2xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3">
                    {selectedProject.name}
                  </h2>
                  <p className="text-sm md:text-base lg:text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                    {selectedProject.description}
                  </p>
                </div>

                {selectedProject.features && selectedProject.features.length > 0 && (
                  <div>
                    <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3 flex items-center gap-1 md:gap-2">
                      <Zap className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-blue-600 flex-shrink-0" />
                      Key Features
                    </h3>
                    <ul className="grid md:grid-cols-2 gap-2 md:gap-3">
                      {selectedProject.features.map((feature, index) => (
                        <li
                          key={index}
                          className="flex items-start text-xs md:text-sm lg:text-base text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-2 md:p-3 lg:p-4 rounded-lg"
                        >
                          <span className="text-blue-500 mr-1 md:mr-2 mt-0.5 font-bold text-lg flex-shrink-0">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedProject.techStack && selectedProject.techStack.length > 0 && (
                  <div>
                    <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white mb-2 md:mb-3 flex items-center gap-1 md:gap-2">
                      <Brain className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-purple-600 flex-shrink-0" />
                      Technology Stack
                    </h3>
                    <div className="flex flex-wrap gap-1 md:gap-2 lg:gap-3">
                      {selectedProject.techStack.map((tech, index) => (
                        <span
                          key={index}
                          className="px-2 md:px-3 lg:px-4 py-1 md:py-2 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs md:text-sm font-bold border border-blue-200 dark:border-blue-800"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedProject.pricing && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-3 md:p-4 lg:p-6">
                    <h3 className="text-sm md:text-base lg:text-lg font-bold text-green-800 dark:text-green-300 mb-1 md:mb-2">
                      Expected Pricing Model
                    </h3>
                    <p className="text-xl md:text-2xl lg:text-3xl font-bold text-green-700 dark:text-green-400">
                      {selectedProject.pricing}
                    </p>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-4 md:px-8 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col md:flex-row items-center gap-2 md:gap-3">
                <button
                  onClick={() => setSelectedProject(null)}
                  className="w-full md:w-auto px-4 md:px-6 lg:px-8 py-3 md:py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm md:text-base min-h-[44px] flex items-center justify-center"
                >
                  Close
                </button>
                <a
                  href="mailto:usa@nevxoaulio.com"
                  className="w-full md:flex-1 px-4 md:px-6 lg:px-8 py-3 md:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg flex items-center justify-center space-x-1 md:space-x-2 text-sm md:text-base min-h-[44px]"
                >
                  <span>Partner With Us</span>
                  <ExternalLink className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* ===== CTA SECTION - Mobile Optimized ===== */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <Rocket className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 md:mb-6" />
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6">
              Build India's Tech Future with NEVXOAULIO VENTURES
            </h2>
            <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8 leading-relaxed max-w-3xl mx-auto">
              We're building 22+ revolutionary products from 2030-2040. Partner with us through investment, collaboration, or talent to transform Indian technology.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
              <a
                href="/contact"
                className="w-full sm:w-auto px-6 md:px-10 py-3 md:py-4 bg-white text-blue-600 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg text-base md:text-lg min-h-[44px] flex items-center justify-center"
              >
                Invest in Our Vision
              </a>
              <a
                href="/about"
                className="w-full sm:w-auto px-6 md:px-10 py-3 md:py-4 bg-transparent border-2 border-white text-white rounded-lg font-bold hover:bg-white/10 transition-colors text-base md:text-lg min-h-[44px] flex items-center justify-center"
              >
                Learn More
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}